import { normalizeDraftManager } from './keepers';
import { ACTIVE_MEMBERS } from './managers';

function emptyDraftOrderState() {
  return {
    published: false,
    isValid: true,
    order: [],
    rounds: [],
    errors: [],
  };
}

function isHtmlFallback(text) {
  return /^\s*<!doctype html/i.test(text) || /^\s*<html/i.test(text);
}

function readField(row, names) {
  return names.map((name) => row?.[name]).find((value) => value !== undefined && value !== null && value !== '');
}

function readInteger(row, names) {
  const value = readField(row, names);
  const number = Number(value);
  return Number.isInteger(number) ? number : null;
}

function readBoolean(row, names) {
  const value = readField(row, names);
  if (value === true || value === 'true' || value === 'TRUE') return true;
  return false;
}

function normalizeManager(name) {
  return normalizeDraftManager(String(name || '').trim());
}

function entryLabel(index) {
  return `draftOrder[${index}]`;
}

function compareEntries(a, b) {
  return a.round - b.round || a.pick - b.pick || (a.overall || 0) - (b.overall || 0);
}

export function validateDraftOrder(entries, options = {}) {
  const managers = options.managers || ACTIVE_MEMBERS;
  const expectedRounds = options.expectedRounds || null;
  const allowTradedPicks = options.allowTradedPicks === true;
  const allowedManagers = new Set(managers);
  const errors = [];
  const order = [];
  const roundState = new Map();
  const seenOverall = new Set();

  if (!Array.isArray(entries)) {
    return {
      published: true,
      isValid: false,
      order: [],
      rounds: [],
      errors: ['Draft order file must be a JSON array.'],
    };
  }

  entries.forEach((entry, index) => {
    const label = entryLabel(index);
    const round = readInteger(entry, ['round', 'Round']);
    const pick = readInteger(entry, ['pick', 'Pick', 'draftPosition', 'Draft_Position']);
    const overall = readInteger(entry, ['overall', 'Overall', 'pickOverall', 'Pick_Overall']);
    const manager = normalizeManager(readField(entry, ['manager', 'Manager', 'team', 'Team', 'owner']));
    const keeperCost = readBoolean(entry, ['keeperCost', 'keeper_cost', 'Keeper_Cost']);

    if (!Number.isInteger(round) || round < 1) {
      errors.push(`${label}: round must be a positive integer.`);
    }

    if (!Number.isInteger(pick) || pick < 1) {
      errors.push(`${label}: pick must be a positive integer.`);
    } else if (pick > managers.length) {
      errors.push(`${label}: pick ${pick} is outside a ${managers.length}-manager round.`);
    }

    if (overall !== null && overall < 1) {
      errors.push(`${label}: overall pick must be a positive integer when provided.`);
    }

    if (!manager) {
      errors.push(`${label}: missing manager.`);
    } else if (!allowedManagers.has(manager)) {
      errors.push(`${manager}: not an active manager for this draft order.`);
    }

    if (!Number.isInteger(round) || round < 1 || !Number.isInteger(pick) || pick < 1 || !allowedManagers.has(manager)) {
      return;
    }

    if (!roundState.has(round)) {
      roundState.set(round, {
        picks: new Map(),
        managers: new Map(),
      });
    }

    const state = roundState.get(round);
    if (state.picks.has(pick)) {
      errors.push(`Round ${round}, pick ${pick}: duplicate draft slot.`);
    }
    state.picks.set(pick, manager);

    if (!allowTradedPicks && state.managers.has(manager)) {
      errors.push(`${manager}: duplicate draft slot in Round ${round}.`);
    }
    state.managers.set(manager, pick);

    if (overall !== null) {
      if (seenOverall.has(overall)) {
        errors.push(`Overall pick ${overall}: duplicate draft slot.`);
      }
      seenOverall.add(overall);
    }

    const normalizedEntry = {
      round,
      pick,
      overall,
      manager,
      keeperCost,
    };
    const teamName = readField(entry, ['teamName', 'Team_Name']);
    if (teamName) normalizedEntry.teamName = teamName;
    order.push(normalizedEntry);
  });

  roundState.forEach((state, round) => {
    if (allowTradedPicks) {
      for (let pick = 1; pick <= managers.length; pick += 1) {
        if (!state.picks.has(pick)) errors.push(`Round ${round}: missing pick ${pick}.`);
      }
    } else {
      managers.forEach((manager) => {
        if (!state.managers.has(manager)) {
          errors.push(`Round ${round}: missing ${manager}.`);
        }
      });
    }
  });

  if (expectedRounds !== null) {
    for (let round = 1; round <= expectedRounds; round++) {
      if (!roundState.has(round)) {
        errors.push(`Round ${round}: missing draft order.`);
      }
    }

    roundState.forEach((_, round) => {
      if (round > expectedRounds) {
        errors.push(`Round ${round}: outside expected ${expectedRounds}-round draft.`);
      }
    });
  }

  const rounds = Array.from(roundState.keys()).sort((a, b) => a - b);

  return {
    published: true,
    isValid: errors.length === 0,
    order: order.sort(compareEntries),
    rounds,
    errors,
  };
}

export async function loadDraftOrder(year = 2026, options = {}) {
  const {
    fetchImpl = fetch,
    managers = ACTIVE_MEMBERS,
    expectedRounds = null,
    allowTradedPicks = false,
  } = options;

  const response = await fetchImpl(`/data/draft-order/${year}.json`);
  if (!response.ok) return emptyDraftOrderState();

  const text = await response.text();
  if (!text || isHtmlFallback(text)) return emptyDraftOrderState();

  let entries;
  try {
    entries = JSON.parse(text);
  } catch {
    return {
      published: true,
      isValid: false,
      order: [],
      rounds: [],
      errors: [`Draft order file for ${year} is not valid JSON.`],
    };
  }

  return validateDraftOrder(entries, { managers, expectedRounds, allowTradedPicks });
}
